
import {
  Vue, Component, PropSync, Prop, Watch,
} from 'vue-property-decorator';
import { debounce } from 'ts-debounce';
import { PartItem } from '@/features/shipment/types';
import { getPartsBook } from '@/features/shipment/services/parts-book.services';
import { showErrorAlertDialog } from '@/features/common/helpers';
import { defaultOffsetLimit } from '@/features/common/raw';
import NoContentWithHint from '@/features/shipment/components/createShipment/NoContentWithHint.vue';
import NoResultsFound from '@/features/common/components/NoResultsFound.vue';
import PartsBookTable from './PartsBookTable.vue';
import EditPartsBookDialog from './EditPartsBookDialog.vue';

@Component({
  components: {
    PartsBookTable,
    NoContentWithHint,
    NoResultsFound,
    EditPartsBookDialog,
  },
})
export default class PartsBook extends Vue {
  @PropSync('show') syncedShow!: boolean;

  @Prop({ required: true }) partsBook!: PartItem[];

  @Prop({ required: false, default: false }) noContent!: boolean;

  private requestCallCount = 1;

  private searchField = '';

  private isLoading = false;

  private hasSearched = false;

  private filteredPartsBook: PartItem[] = [];

  private showEditDialog = false;

  private selectedPartsBookItem: PartItem | null = null;

  @Watch('partsBook.length', { immediate: true })
  private updateFilteredPartsBook() {
    this.filteredPartsBook = [...this.partsBook];
  }

  private searchPartsBookItem(value: string) {
    this.debounceSearch(value);
  }

  private debounceSearch = debounce(this.debounceSearchHandler, 500);

  private async debounceSearchHandler(value: string): Promise<void> {
    this.isLoading = true;

    const items = await getPartsBook({
      search: value?.trim(),
    });
    if (items === null) {
      showErrorAlertDialog();
    } else {
      this.filteredPartsBook = [...items];
      this.hasSearched = true;
    }

    this.isLoading = false;
  }

  private editPartsBook(partsBookItem: PartItem): void {
    this.selectedPartsBookItem = partsBookItem;
    this.showEditDialog = true;
  }

  private handlePartsBookUpdated(updatedPartsBook: PartItem): void {
    const index = this.partsBook.findIndex((item) => item.id === updatedPartsBook.id);
    if (index !== -1) {
      const updatedPartsBookList = [...this.partsBook];
      updatedPartsBookList[index] = updatedPartsBook;
      this.$emit('update', updatedPartsBookList);
      this.filteredPartsBook = [...updatedPartsBookList];
    }
  }

  private deletePartsBook(id: number) {
    const items = this.partsBook.filter((item) => item.id !== id);
    this.$emit('update', items);
  }

  private async fetchMorePartsBookItem() {
    this.isLoading = true;

    const partsBookItems = await getPartsBook({
      offset: defaultOffsetLimit * this.requestCallCount,
    });

    if (partsBookItems && partsBookItems.length) {
      this.$emit('update', [
        ...this.partsBook,
        ...partsBookItems,
      ]);
      this.requestCallCount += 1;
    }

    this.isLoading = false;
  }
}
